'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { getPickerIcon } from '@/lib/picker-icons';
import { TicketCardRow } from './TicketCard';
import type { Status } from 'dico-types';
import type { Ticket } from 'dico-types';

export const NO_STATUS_ID = '__none__';

/** Status with optional id for "No status" grouping */
export type StatusGroup =
  | Status
  | { id: typeof NO_STATUS_ID; name: string; color?: string; icon?: string };

function getStatusGroupId(ticket: Ticket): string {
  return ticket.statusId ?? NO_STATUS_ID;
}

interface TicketListByStatusProps {
  tickets: Ticket[];
  statusGroups: StatusGroup[];
  workspaceId: string;
  /** Default open for sections (all true = all expanded) */
  defaultOpen?: Record<string, boolean>;
}

function getStatusGroupLabel(group: StatusGroup): string {
  if ('id' in group && group.id === NO_STATUS_ID) return group.name;
  return group.name;
}

export function TicketListByStatus({
  tickets,
  statusGroups,
  workspaceId,
  defaultOpen = {}
}: TicketListByStatusProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const g of statusGroups) {
      initial[g.id] = defaultOpen[g.id] ?? true;
    }
    return initial;
  });

  const ticketsByStatus = new Map<string, Ticket[]>();
  for (const g of statusGroups) {
    ticketsByStatus.set(g.id, []);
  }
  for (const t of tickets) {
    const id = getStatusGroupId(t);
    if (!ticketsByStatus.has(id)) ticketsByStatus.set(id, []);
    ticketsByStatus.get(id)!.push(t);
  }

  return (
    <div className="flex flex-col gap-2">
      {statusGroups.map((group) => {
        const id = group.id;
        const label = getStatusGroupLabel(group);
        const count = ticketsByStatus.get(id)?.length ?? 0;
        const isOpen = openSections[id] ?? true;
        const iconType = getPickerIcon(group.icon) ?? 'Circle';
        return (
          <Collapsible
            key={id}
            open={isOpen}
            className={'shadow-xs rounded-lg border p-1'}
            // style={
            //   group.color
            //     ? {
            //         backgroundColor: `color-mix(in srgb, ${group.color} 5%, transparent)`,
            //         borderColor: `${group.color}10`
            //       }
            //     : undefined
            // }
            onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, [id]: open }))}
          >
            <CollapsibleTrigger className={'w-full rounded-sm'}>
              <div className={`flex w-full items-center gap-2 p-1.5`}>
                <span className="text-muted-foreground shrink-0">
                  <MonoIcon
                    type={isOpen ? 'ChevronDown' : 'ChevronRight'}
                    // style={group.color ? { color: group.color + '80' } : undefined}
                  />
                </span>
                <MonoIcon
                  type={iconType}
                  style={group.color ? { color: group.color } : undefined}
                />
                <span className="text-foreground text-sm font-medium">{label}</span>
                <span className="text-foreground text-xs">{count}</span>
              </div>
            </CollapsibleTrigger>
            {count > 0 && (
              <CollapsibleContent
                className={'bg-card shadow-xs borde mt-0.5 rounded-md border p-0'}
              >
                <div className="flex flex-col gap-0">
                  {(ticketsByStatus.get(id) ?? []).map((ticket) => (
                    <TicketCardRow key={ticket.id} ticket={ticket} workspaceId={workspaceId} />
                  ))}
                </div>
              </CollapsibleContent>
            )}
          </Collapsible>
        );
      })}
    </div>
  );
}
